import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Spin } from "antd";
import Card, { CardHeader, CardBody } from "@components/card";
import { find, get } from "lodash";

import "./notes.scss";
import { TNotesCardProps } from "../constant";
// import moment from "moment";
import membersService from "@services/members.service";
import { centricClientCheck, formatDisplayDate } from "@utils/helpers";
import { useLocation, useNavigate } from "react-router-dom";
import { routeMap, READ_ONLY_DRAWER_NOTES } from "@utils/constants";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import { CanEdit } from "@components/accessLevel";
import useSession from "@utils/useSession";

const NotesCard: FC<TNotesCardProps> = ({
  className,
  width,
  avatarSize,
  bookmarkClickHandler,
  data,
  noteEditor,
  setNoteEditState,
  membersChangeHandler,
  setContent,
  deleteHandler,
  setEditNoteData,
  setMembersOptions,
  setEditMode,
  setBookmarkStatus,
  setRecipientType,
  setPrevBookmarkStatus,
  tab,
  onShowMore,
  rightDrawerCloseHandler
}: TNotesCardProps) => {
  const { patientId, patientName, notes, notesCreateDate, isBookMarked, notesId, noteType, careMgrFirstName, careMgrLastName, recipientType , careManagerId, patientStatus } = data;
  // const [showMoreBtn, showMoreHandler] = useState(false);
  const [bookmarkLoader, setBookmarkLoader] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(true);
  const [noteToDelete, setNoteToDelete] = useState<number>();
  const [patientFetchLoader, setPatientFetchLoader] = useState<boolean>(false);
  // const [addLoader, setAddLoader] = useState(false);
  const charLength = 70;

  const location = useLocation();
  const navigate = useNavigate();
  const session = useSession();

  const isMemberDetails = location.pathname === "/member-detail";
  const isLoggedInUser = careManagerId === session?.user?.id;

  const bookmarkNotes = (item: any) => {
    setBookmarkLoader(true);
    membersService.bookmarkNotes(item.notesId, !item.isBookMarked).subscribe({
      next: () => {
        setBookmarkLoader(false);
        bookmarkClickHandler({ notesId: item.notesId, patientName, patientId, isBookMarked: !item.isBookMarked });
      },
      error: () => {
        setBookmarkLoader(false);
      },
    });
  };

  // const showMore = (flag: boolean) => {
  //   showMoreHandler(flag);
  // };

  const content = useRef<HTMLDivElement>(null)

  const toggleShowMore = () => {
    if (content.current) {
      content.current.scrollTop = 0
    }
    // showMore(showMoreBtn ? false : true)
    onShowMore(data.isShowMore ? false : true)
  }
   
  const [contentHeight, setContentHeight] = useState(0)
   
  useEffect(
    () => {
      setTimeout(
        () => {
          setContentHeight(content.current?.scrollHeight || 0)
        },
        50
      )
    },
    [notes]
  )

  const getContentView = () => {
    const noteItem: any = <div dangerouslySetInnerHTML={{ __html: notes }}></div>;

    return (
      <>
        <div className={`noteContent custom-scroll-bar notes-html ${data.isShowMore ? 'more' : 'less'}`} ref={content}>
          {noteItem}
        </div>
        {
          contentHeight > 40 &&
        <div className="noteContent__showMoreLink" onClick={() => toggleShowMore()}>
          {data.isShowMore ? 'less' : 'more'}
        </div>
        }
      </>
    )

  };

  const onEdit = () => {
    setNoteEditState(true);
    membersChangeHandler([patientId]);
    setContent(notes);
    // setAddLoader(true);
    setEditNoteData(data);
    setMembersOptions([
      {
        patient_id: data.patientId,
        patient_name: data.patientName,
      },
    ]);
    setEditMode(true);
    setBookmarkStatus({ isBookmarked: isBookMarked})
    setPrevBookmarkStatus({ isBookmarked: isBookMarked})
    setRecipientType(recipientType);
  };
  
  const deleteNote = () => {
    setDeleteLoader(true);
    setNoteToDelete(notesId);
    deleteHandler(notesId, setDeleteLoader);
  };

  const goToMemberDetails = () => {
    if (isMemberDetails) {
      return;
    }
    setPatientFetchLoader(true);
    membersService.membersByIds([patientId]).subscribe({
      next: (v) => {
        let member = v?.data?.data?.patientList.pop();
        if (member) {
          membersService.setCurrentMember = member;
          membersService.setsingleMember = false;
          navigate(routeMap.memberProfile);
          rightDrawerCloseHandler();
          setPatientFetchLoader(false);
        }
      },
      error: () => {
        setPatientFetchLoader(false);
      },
    });
  };

  const getNoteHeaderClassName = () => {
    const baseClass:any = `notesCardHeader`
    if(isLoggedInUser){
      if(recipientType){
        return  `notesCardPcpHeader ${baseClass}`;
      }
      else return `${baseClass}`;
    }
    else return `non-editable ${baseClass}`;
  }
  
  return (
    <Card className={`${className} note ${data.isShowMore ? "" : "showless"}`} width={width} id={`drawer_note_card_${tab}_${notesId}`}>
      <Spin spinning={patientFetchLoader} indicator={<HealthemSpinner size={25} />}>
        <CardHeader className={getNoteHeaderClassName()}>
          {/* <Avatar className="card-avatar">{patientName?.charAt(0)}</Avatar> */}
          <span className="clr-blueLink w500" style={{ cursor: isMemberDetails ? "auto" : "pointer" }} onClick={goToMemberDetails}>
            {patientName}
          </span>
          {noteEditor && (
            <div className="action">
              {isLoggedInUser && (
                <>
                  <Spin spinning={bookmarkLoader} indicator={<HealthemSpinner size={20} />}>
                    <span
                      style={{ height: "0.4em" }}
                      className={`pointer f20 clr-themeColor ${isBookMarked ? "icon-bookmark-solid" : "icon-bookmark bookmark-icon-hidden"} `}
                      onClick={() => {
                        bookmarkNotes(data);
                      }}
                    />
                  </Spin>
                  <CanEdit>
                    {find(READ_ONLY_DRAWER_NOTES, { noteType: String(noteType) }) || (centricClientCheck() && patientStatus === "0") ? null : (
                      <span className="action__secondary-options">
                        <span
                          className="btn-icon icon-edit f18 clr-blueLink pointer"
                          onClick={() => {
                            onEdit();
                          }}
                        />
                        <Spin spinning={deleteLoader && notesId === noteToDelete} indicator={<HealthemSpinner size={20} />}>
                          <span className="btn-icon icon-delete f18 clr-e100 pointer" onClick={deleteNote} />
                        </Spin>
                      </span>
                    )}
                  </CanEdit>
                </>
              )}
            </div>
          )}
        </CardHeader>
      </Spin>
      <CardBody className="notesCardBody">
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>
            <span className="f10 note-mgrNameColor">
              {careMgrFirstName} {careMgrLastName}{" "}
            </span>
            <span className="italic f10 clr-lightGrayColor">| {formatDisplayDate(notesCreateDate, "reminderDateActualFormat", "defaultDateFormatWithTimeWithPipe", true)}</span>
          </span>
          {find(READ_ONLY_DRAWER_NOTES, { noteType: String(noteType) }) && get(find(READ_ONLY_DRAWER_NOTES, { noteType: String(noteType) }), "noteLabel") ? (
            <div className="notesCardBody__origin-title">{get(find(READ_ONLY_DRAWER_NOTES, { noteType: String(noteType) }), "noteLabel")}</div>
          ) : null}
        </div>
        {getContentView()}
      </CardBody>
    </Card>
  );
};

export default NotesCard;
